import { analytics, trackEvent } from "../../services/analytics";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import BanksMarquee from "./BanksMarquee";
import PrinterAnimation from "./PrinterAnimation";
import AdPlayer from "../ads/AdPlayer";
import { useAdPlaylist } from "../ads/AdsSync";
import { PLACEMENTS } from "../ads/adPlaylist";
import { cancelPaymentAction, createPaymentAction, fetchPaymentStatus } from "../../store/actions/paymentsActions";
import { useKioskToast, KioskToast } from "../../components/useKioskToast";
import botQr from "../../assets/img/mangerQR.png";
import { acceptPrintStatus, markPrintAbandoned } from "./printSession";

const ACTIVE_STATUSES = ["started", "submitted", "printing"];
// Статусы платежа (GET /payments/status), после которых опрашивать больше
// нечего — исход уже известен. "completed" сюда тоже входит: как только
// оплата подтверждена, дальнейшую судьбу заказа ведёт печать (Electron
// print-status), а не этот опрос.
const TERMINAL_PAYMENT_STATUSES = ["completed", "failed", "expired", "lost", "cancelled"];
// Раз в сколько опрашивать статус платежа, пока не пришёл терминальный —
// тот же интервал, с которым sidecar опрашивает сам Kaspi (polling.js).
const PAYMENT_STATUS_POLL_MS = 3000;
// Сколько раз пробуем перезапросить саму картинку, прежде чем выпускать
// платёж заново: если ссылка валидна, чаще всего виноват разовый сетевой
// сбой, и повторная загрузка дешевле нового платежа (POST /create не
// идемпотентен — каждый вызов создаёт новую запись с новым order_id).
const MAX_IMG_RETRIES = 2;
// Если за это время не пришёл ни "completed", ни "error" (принтер не
// ответил, либо ответ есть, а колбэк потерялся — см. INTEGRATION_ISSUES.md),
// анимация печати не должна крутиться бесконечно — спрашиваем пользователя.
// Минута, а не меньше: многостраничная печать честно идёт дольше, и вопрос
// «Документ вышел?» посреди нормальной печати только сбивает с толку.
export const PRINT_STATUS_TIMEOUT_MS = 60000;
// Единая задержка для печати с рекламой и без неё, от первого активного статуса.
export const PRINT_BACK_DELAY_MS = 5000;
// Куда ведёт «Назад» с экрана печати: к списку документов, сессия остаётся.
const DOCUMENTS_ROUTE = "/printing/show_list/user";

const PAGE = "h-[88vh] px-[2.4vw] py-[2vh] flex flex-col bg-[#EDEFF4]";
const CARD =
    "bg-white rounded-[3vh] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)]";

const blockVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.45, delay: i * 0.12 },
    }),
};

// mm:ss, минимум "0:00" (не уходит в минус — счётчик просто останавливается
// на нуле, реальную "протух ли QR" границу подтверждает опрос статуса, а не
// этот таймер: он только показывает, а не решает).
function formatCountdown(seconds) {
    const clamped = Math.max(0, Math.floor(seconds));
    const m = Math.floor(clamped / 60);
    const s = clamped % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
}

// Живой отсчёт до targetIso. Раньше в CLAUDE.md/PROJECT_STATUS.md было
// зафиксировано, что такой таймер невозможен — "данных не существует": ни
// ExpireDate, ни QrPaymentBehaviorOptions от Kaspi нигде не передавались
// киоску. Теперь передаются (см. kaspi_qr.py::create_unified_qr,
// KaspiQrImg.expires_at) — оба документа обновлены вместе с этой правкой.
function useCountdownSeconds(targetIso) {
    const [secondsLeft, setSecondsLeft] = useState(null);

    useEffect(() => {
        if (!targetIso) {
            setSecondsLeft(null);
            return undefined;
        }
        const targetMs = new Date(targetIso).getTime();
        if (Number.isNaN(targetMs)) {
            setSecondsLeft(null);
            return undefined;
        }
        const tick = () => setSecondsLeft(Math.max(0, (targetMs - Date.now()) / 1000));
        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [targetIso]);

    return secondsLeft;
}

function PrintBackControl({ secondsLeft, onBack }) {
    const { t } = useTranslation();
    const ready = secondsLeft === 0;
    // Кнопка занимает постоянное место и остаётся видимой до разблокировки.
    return (
        <div className="flex-none h-[7.4vh]">
            <motion.button type="button" data-analytics="paymentpage.footer.back.8"
                disabled={!ready}
                aria-label={t("footer.back")}
                aria-describedby={ready ? undefined : "print-back-countdown"}
                initial={{ y: 12 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.35 }}
                onClick={onBack}
                className="w-full h-full rounded-[3vh] bg-white shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)] flex items-center justify-center gap-[0.8vw] text-[2.2vh] font-bold text-[#12161F] enabled:active:bg-[#F1F3F7] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
                <svg
                    className="w-[2.4vh] h-[2.4vh]"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M19 12H5M11 6l-6 6 6 6" />
                </svg>
                {t("footer.back")}
                {!ready && <span id="print-back-countdown" role="timer" className="text-[1.6vh] font-semibold tabular-nums">
                    {t("paymentPage.back_available_in", { count: secondsLeft })}
                </span>}
            </motion.button>
        </div>
    );
}

function PaymentPage() {
    const { t } = useTranslation();
    const { payments, lastPaymentRequest, loadingPayment, paymentStatus } = useSelector(
        (state) => state.payments,
    );
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const isEmpty = !payments || Object.keys(payments).length === 0;

    const [printStatus, setPrintStatus] = useState(null);
    const [printMessage, setPrintMessage] = useState("");
    const [countdown, setCountdown] = useState(10);
    // Исход печати не подтверждён колбэком за отведённое время — спрашиваем
    // пользователя, дошёл ли документ до лотка.
    const [outcomeUnknown, setOutcomeUnknown] = useState(false);
    const [issueReported, setIssueReported] = useState(false);
    // Ответ SmartVend может прийти текстом ошибки в поле img с HTTP 200
    // (см. pars_img_qr.py) — отдельно от сетевого/decode-сбоя самого <img>.
    const [qrLoadFailed, setQrLoadFailed] = useState(false);
    // Счётчик попыток перезагрузить картинку. Пишется в key/URL <img>,
    // чтобы браузер пошёл за ней заново, а не отдал неудачу из кэша.
    const [imgRetries, setImgRetries] = useState(0);
    // Реклама на экране печати: ролики с отметкой «Печать», уже скачанные в
    // локальный кэш. Пусто — экран печати остаётся прежним, с большим принтером.
    const { items: printAds } = useAdPlaylist(PLACEMENTS.print);
    // Последний статус для cleanup-а подписки: ушли посреди печати или нет.
    const analyticsOrderRef = useRef(payments?.order_id);
    analyticsOrderRef.current = payments?.order_id;
    const printStatusRef = useRef(printStatus);
    printStatusRef.current = printStatus;
    const [printBackSeconds, setPrintBackSeconds] = useState(PRINT_BACK_DELAY_MS / 1000);
    const printStartedAtRef = useRef(null);

    useEffect(() => {
        if (isEmpty) {
            const timer = setTimeout(() => {
                navigate("/", { replace: true });
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [isEmpty, navigate]);

    useEffect(() => {
        // electronAPI.removeListener не существует в preload (см. INTEGRATION_ISSUES.md) —
        // ipcRenderer-подписка реально не снимается. mountedRef хотя бы не даёт
        // событию, пришедшему после ухода со страницы, обновить её состояние.
        let mountedRef = true;
        const handlePrintStatus = (event, data) => {
            if (!mountedRef) return;
            // Хвост задания, с которого ушли посреди печати, — не наш заказ.
            if (!acceptPrintStatus(data.status)) return;
            trackEvent("print_status", { status: data.status, source: "electron",
                ...(analyticsOrderRef.current != null ? { order_id: String(analyticsOrderRef.current) } : {}) });
            setPrintStatus(data.status);
            setPrintMessage(data.message || "");
        };

        window.electronAPI?.onPrintStatus(handlePrintStatus);
        return () => {
            mountedRef = false;
            if (ACTIVE_STATUSES.includes(printStatusRef.current)) markPrintAbandoned();
            window.electronAPI?.removeListener?.("print-status", handlePrintStatus);
        };
    }, []);

    const isActivePrint = ACTIVE_STATUSES.includes(printStatus);
    const showsPrintAds = isActivePrint && printAds.length > 0;

    useEffect(() => {
        if (!isActivePrint) {
            printStartedAtRef.current = null;
            setPrintBackSeconds(PRINT_BACK_DELAY_MS / 1000);
            return undefined;
        }
        if (printStartedAtRef.current === null) printStartedAtRef.current = Date.now();
        const tick = () => {
            const remaining = PRINT_BACK_DELAY_MS - (Date.now() - printStartedAtRef.current);
            setPrintBackSeconds(Math.max(0, Math.ceil(remaining / 1000)));
            if (remaining <= 0) clearInterval(timer);
        };
        const timer = setInterval(tick, 250);
        tick();
        return () => clearInterval(timer);
    }, [isActivePrint]);

    // Опрос статуса платежа — раньше киоск не имел ни одного способа узнать
    // судьбу платежа сам, только косвенно наблюдал начало печати. Опрашиваем
    // только Единый QR (у SmartVend нет promежуточных статусов вроде
    // "scanned" — подтверждение приходит тем же путём, что и раньше,
    // POST /print/callback) и только пока Electron ещё ничего не сказал:
    // как только пришёл printStatus, судьбу заказа дальше ведёт печать.
    const orderId = payments?.order_id;
    const orderProvider = payments?.provider;
    const currentPaymentStatus =
        paymentStatus?.order_id === orderId ? paymentStatus?.status : null;
    useEffect(() => {
        if (currentPaymentStatus && orderId) {
            trackEvent("payment_status", { status: currentPaymentStatus, order_id: String(orderId), provider: orderProvider, source: "server_status" });
        }
    }, [currentPaymentStatus, orderId, orderProvider]);
    useEffect(() => {
        if (printStatus) return undefined;
        if (orderProvider !== "kaspi_qr" || !orderId) return undefined;
        if (currentPaymentStatus && TERMINAL_PAYMENT_STATUSES.includes(currentPaymentStatus)) {
            return undefined;
        }

        let cancelled = false;
        const poll = () => {
            if (!cancelled) dispatch(fetchPaymentStatus({ orderId, provider: orderProvider }));
        };
        poll();
        const interval = setInterval(poll, PAYMENT_STATUS_POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [dispatch, printStatus, orderId, orderProvider, currentPaymentStatus]);

    // Если статус "в печати" висит дольше PRINT_STATUS_TIMEOUT_MS без
    // completed/error — считаем колбэк потерянным и спрашиваем пользователя
    // напрямую, а не крутим анимацию бесконечно.
    useEffect(() => {
        if (!isActivePrint) {
            setOutcomeUnknown(false);
            setIssueReported(false);
            return;
        }
        const timer = setTimeout(() => {
            setOutcomeUnknown(true);
        }, PRINT_STATUS_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [isActivePrint]);

    // По завершении печати — обратный отсчёт и выход на главную с очисткой сессии.
    useEffect(() => {
        if (printStatus !== "completed") return;

        setCountdown(10);
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    analytics.endSession("logout");
                    localStorage.removeItem("access_token");
                    navigate("/");
                    return 10;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [printStatus, navigate]);

    const handleGoHome = () => {
        analytics.endSession("logout");
        localStorage.removeItem("access_token");
        navigate("/");
    };

    const handlePrintAgain = () => {
        setCountdown(10);
        navigate(DOCUMENTS_ROUTE);
    };

    // «Назад» посреди печати: к документам, сессия остаётся. Печать идёт
    // дальше в Electron, а её хвост отсекает printSession (см. cleanup подписки).
    const handleBackFromPrint = () => {
        navigate(DOCUMENTS_ROUTE);
    };

    // Пользователь подтвердил, что документ вышел, хотя колбэк не пришёл —
    // ведём себя так же, как при настоящем "completed" (тот же экран, тот же
    // отсчёт до выхода на главную).
    const handleConfirmPrinted = () => {
        trackEvent("print_user_confirmed", { source: "user", ...(orderId != null ? { order_id: String(orderId) } : {}) });
        setOutcomeUnknown(false);
        setPrintStatus("completed");
    };

    const handleReportIssue = () => {
        trackEvent("print_issue_reported", { source: "user", ...(orderId != null ? { order_id: String(orderId) } : {}) });
        setIssueReported(true);
    };

    const isCompleted = printStatus === "completed";
    const isError = printStatus === "error";
    // Ждём подтверждения от пользователя только пока реальный колбэк ещё не
    // пришёл — если он придёт позже (completed/error), эти экраны выше по
    // приоритету и просто перекроют собой этот блок.
    const isWaitingConfirmation =
        outcomeUnknown && !isCompleted && !isError;

    const qrSrc = payments?.img;
    const qrLooksValid =
        typeof qrSrc === "string" &&
        (qrSrc.startsWith("data:") || qrSrc.startsWith("http"));

    // scanned/lost — экраны ниже показываются только пока Electron ничего не
    // ответил (см. guard printStatus в эффекте опроса выше); как только
    // придёт printStatus, эти блоки в рендере уже не достигаются —
    // isActivePrint/isCompleted/isError возвращаются раньше.
    const isScanned = currentPaymentStatus === "scanned";
    // failed/expired теперь НЕ отдельный экран — платёж молча перевыпускается
    // (см. эффект автообновления ниже), пользователь видит только лёгкий
    // спиннер поверх QR и тост «QR обновлён». Отдельный блокирующий экран с
    // кнопкой остался только у lost — там исход неизвестен (деньги могли
    // списаться), автоматика не может сама решить, перевыпускать платёж или
    // нет, нужен человек.
    const isPaymentFailedOrExpired =
        currentPaymentStatus === "failed" || currentPaymentStatus === "expired";
    const isPaymentLost = currentPaymentStatus === "lost";

    // Свежее значение — из опроса (обновляется каждые 3с); пока первый ответ
    // ещё не пришёл, используем то, что уже было в ответе на само /create.
    const expiresAtIso =
        (paymentStatus?.order_id === orderId ? paymentStatus?.expires_at : null) ??
        payments?.expires_at ??
        null;
    const secondsLeft = useCountdownSeconds(expiresAtIso);

    const { toastMsg, toastTone, showToast } = useKioskToast();
    // Отдельно от showQrError ниже (та — про саму картинку): если
    // автоперевыпуск после failed/expired сам не удался (сеть, антиспам-
    // отказ сервера), падаем в тот же компактный "битый QR" вид с кнопкой
    // вручную — не изобретаем второй, ещё один экран ошибки.
    const [autoReissueFailed, setAutoReissueFailed] = useState(false);
    // Гейт от повторного дублирующего запроса — не через зависимость на
    // loadingPayment/canReissuePayment (эффект бы перезапускался ровно от
    // того же dispatch, который сам loadingPayment и выставляет, и тогда
    // cleanup гасил бы ещё не отработавший запрос, а не устаревший).
    const autoReissueInFlightRef = useRef(false);
    // Идёт отмена кнопкой «Отменить» — автоперевыпуск не должен в этот момент
    // выпустить новый QR, который останется висеть на сервере после ухода.
    const cancellingRef = useRef(false);
    const [cancelling, setCancelling] = useState(false);

    useEffect(() => {
        setAutoReissueFailed(false);
    }, [orderId]);

    // Автоперевыпуск QR при failed/expired — тихо, без участия пользователя.
    // Раньше здесь был блокирующий экран «Оплата не завершена» с кнопкой
    // «Получить новый QR»: пользователь просил вместо этого просто получать
    // новый код и небольшое уведомление о том, что он обновился.
    useEffect(() => {
        if (!isPaymentFailedOrExpired) return;
        if (autoReissueInFlightRef.current || cancellingRef.current) return;
        if (!lastPaymentRequest) return;

        autoReissueInFlightRef.current = true;
        dispatch(createPaymentAction(lastPaymentRequest)).then((action) => {
            autoReissueInFlightRef.current = false;
            if (createPaymentAction.fulfilled.match(action)) {
                setAutoReissueFailed(false);
                setQrLoadFailed(false);
                setImgRetries(0);
                showToast(t("paymentPage.qr_refreshed"), "accent");
            } else {
                // Не повторяем сами — antispam на сервере и так уже мог
                // отказать (429), долбить его ещё раз молча было бы той же
                // спам-кнопкой, только автоматической. Ручной путь остаётся:
                // showQrError ниже включит компактную карточку с retry.
                setAutoReissueFailed(true);
            }
        });
        // `t`/`showToast` не в зависимостях: `t` — по той же причине, что и
        // везде в проекте (см. CLAUDE.md, "t в зависимостях useEffect"),
        // `showToast` вызывается только внутри уже стартовавшего запроса, не
        // должен сам по себе перезапускать эффект. Настоящие триггеры —
        // сам факт failed/expired и наличие payload для повтора.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPaymentFailedOrExpired, lastPaymentRequest, dispatch]);

    const showQrError = !qrLooksValid || qrLoadFailed || autoReissueFailed;
    // Показываем поверх ЕЩЁ действующей (пусть и обречённой) картинки, пока
    // тихий автоперевыпуск не закончился — вместо резкой смены на пустую
    // карточку ошибки. showQrError к этому моменту ещё false (autoReissueFailed
    // выставится только если попытка провалилась).
    const showAutoReissueSpinner = isPaymentFailedOrExpired && loadingPayment && !showQrError;

    // "kaspi_qr" — Единый QR (printbox_kaspi_pos), принимает любой банк.
    // "smartvend" (или отсутствует — старый ответ до этого поля) — прежний
    // способ, читается только приложением Kaspi.kz. См. .claude/CLAUDE.md.
    const isUnifiedQr = payments?.provider === "kaspi_qr";

    // Кнопка «Обновить QR-код» на карточке ошибки. Сначала пробуем дешёвый
    // путь — перезапросить саму картинку (платёж уже создан и живёт под своим
    // order_id). Если ссылки нет вовсе (бэкенд положил в img текст ошибки,
    // см. pars_img_qr.py) или перезагрузка не помогла — выпускаем платёж
    // заново тем же payload. Тот же путь служит ручным запасным вариантом,
    // если автоперевыпуск выше сам не удался (autoReissueFailed).
    const canReissuePayment = Boolean(lastPaymentRequest) && !loadingPayment;
    const canRetryImage = qrLooksValid && imgRetries < MAX_IMG_RETRIES && !autoReissueFailed;
    const canRetryQr = canRetryImage || canReissuePayment;

    const handleRetryQr = () => {
        if (canRetryImage) {
            setQrLoadFailed(false);
            setImgRetries((count) => count + 1);
            return;
        }
        if (!canReissuePayment) return;
        setQrLoadFailed(false);
        setImgRetries(0);
        setAutoReissueFailed(false);
        dispatch(createPaymentAction(lastPaymentRequest));
    };

    // «Отменить» вместо «Назад» в футере: заказ отменяется на сервере
    // (POST /payments/cancel), и человек возвращается к параметрам печати —
    // EditManyDocs поднимет их из orderDraft. Просто уйти назад нельзя: живой
    // Единый QR переиспользуется сервером, и заказ с другими параметрами
    // упирался в 409 до истечения старого QR.
    const handleCancelPayment = async () => {
        if (cancellingRef.current) return;
        cancellingRef.current = true;
        setCancelling(true);

        if (orderId) {
            const action = await dispatch(
                cancelPaymentAction({ orderId, provider: orderProvider || "smartvend" }),
            );
            // 404 — заказа на сервере уже нет, отменять нечего.
            if (!cancelPaymentAction.fulfilled.match(action) && action.payload?.status !== 404) {
                cancellingRef.current = false;
                setCancelling(false);
                showToast(
                    t(action.payload?.status === 409 ? "paymentPage.cancel_refused" : "paymentPage.cancel_error"),
                    "danger",
                );
                return;
            }
        }

        const canGoBack = (window.history.state?.idx ?? 0) > 0;
        if (canGoBack) navigate(-1);
        else navigate(location.state?.from || "/printing/show_list/user");
    };

    // Кнопка не показывается, когда оплата уже подтверждена (печать вот-вот
    // начнётся) — сервер всё равно ответил бы 409.
    const canCancelPayment = currentPaymentStatus !== "completed";

    // data:-картинку перезапрашивать бессмысленно (она уже целиком в ответе),
    // а вот у http-ссылки нужно сбить кэш, иначе браузер вернёт ту же неудачу.
    const qrDisplaySrc =
        qrLooksValid && imgRetries > 0 && qrSrc.startsWith("http")
            ? `${qrSrc}${qrSrc.includes("?") ? "&" : "?"}_retry=${imgRetries}`
            : qrSrc;

    if (isEmpty) {
        return (
            <div className={`${PAGE} items-center justify-center`}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className={`${CARD} px-[4vw] py-[3vh] text-center text-[1.8vh] font-bold text-[#C2543E] max-w-[80vw]`}
                >
                    {t("paymentPage.error_data_not_found")}
                </motion.div>
            </div>
        );
    }

    // ── Экран 9 · Готово ─────────────────────────────────────────────
    if (isCompleted) {
        return (
            <div className={`${PAGE} gap-[2vh]`}>
                <div className="flex-1 min-h-0 flex flex-col items-center justify-center text-center">
                    <motion.div
                        initial={{ scale: 0.4, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
                        className="w-[18vh] h-[18vh] rounded-full bg-[#246CE2] flex items-center justify-center mb-[3.6vh]"
                    >
                        <svg
                            className="w-[8.6vh] h-[8.6vh]"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="2.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M4 12.5l5 5L20 6.5" />
                        </svg>
                    </motion.div>

                    <motion.h1
                        variants={blockVariants}
                        initial="hidden"
                        animate="visible"
                        custom={1}
                        className="text-[6vh] font-extrabold tracking-tight"
                    >
                        {t("paymentPage.done_title")}
                    </motion.h1>
                    <motion.p
                        variants={blockVariants}
                        initial="hidden"
                        animate="visible"
                        custom={2}
                        className="text-[2.4vh] font-semibold text-[#7B8290] mt-[1.6vh] max-w-[70vw] leading-snug"
                    >
                        {t("paymentPage.done_sub")}
                    </motion.p>

                    <motion.div
                        variants={blockVariants}
                        initial="hidden"
                        animate="visible"
                        custom={3}
                        className={`${CARD} !rounded-full mt-[3.8vh] flex items-center gap-[1.2vw] px-[3.2vw] py-[1.8vh] text-[2vh] font-bold`}
                    >
                        <svg
                            className="w-[2.8vh] h-[2.8vh] flex-none"
                            style={{ animation: "trayBob 1.2s ease-in-out infinite" }}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#246CE2"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M12 4v13M6 12l6 6 6-6" />
                        </svg>
                        {t("paymentPage.tray_hint")}
                    </motion.div>
                </div>

                <div className="flex-none flex flex-col items-center gap-[1.6vh]">
                    <div className="text-[1.7vh] font-semibold text-[#7B8290]">
                        {t("paymentPage.redirecting_in", { count: countdown })}
                    </div>
                    <div className="w-full flex gap-[1.2vw]">
                        <button data-analytics="paymentpage.paymentPage.print_again.1"
                            type="button"
                            onClick={handlePrintAgain}
                            className="flex-1 py-[2.4vh] rounded-[3vh] bg-[#F1F3F7] text-[2.2vh] font-bold text-[#12161F] active:bg-[#E1E4EB] transition-colors"
                        >
                            {t("paymentPage.print_again")}
                        </button>
                        <button data-analytics="paymentpage.paymentPage.go_home.2"
                            type="button"
                            onClick={handleGoHome}
                            className="flex-1 py-[2.4vh] rounded-[3vh] bg-[#246CE2] text-[2.2vh] font-bold text-white active:bg-[#1B55B4] transition-colors"
                        >
                            {t("paymentPage.go_home")}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Ошибка печати ────────────────────────────────────────────────
    if (isError) {
        return (
            <div className={`${PAGE} gap-[2vh]`}>
                <div className="flex-1 min-h-0 flex flex-col items-center justify-center text-center">
                    <motion.div
                        initial={{ scale: 0.6, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.4 }}
                        className="w-[16vh] h-[16vh] rounded-full bg-[#F7E1DC] flex items-center justify-center mb-[3vh]"
                    >
                        <svg
                            className="w-[7.6vh] h-[7.6vh]"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#C2543E"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                        >
                            <circle cx="12" cy="12" r="9" />
                            <path d="M12 7v6M12 16.5v.01" />
                        </svg>
                    </motion.div>
                    <h1 className="text-[3.6vh] font-extrabold tracking-tight text-[#C2543E]">
                        {t("paymentPage.print_error")}
                    </h1>
                    <p className="text-[2vh] font-semibold text-[#7B8290] mt-[1.4vh] max-w-[70vw]">
                        {printMessage || t("paymentPage.error_sub")}
                    </p>
                </div>

                <div className="flex-none w-full flex gap-[1.2vw]">
                    <button data-analytics="paymentpage.paymentPage.print_again.3"
                        type="button"
                        onClick={handlePrintAgain}
                        className="flex-1 py-[2.4vh] rounded-[3vh] bg-[#F1F3F7] text-[2.2vh] font-bold text-[#12161F] active:bg-[#E1E4EB] transition-colors"
                    >
                        {t("paymentPage.print_again")}
                    </button>
                    <button data-analytics="paymentpage.paymentPage.go_home.4"
                        type="button"
                        onClick={handleGoHome}
                        className="flex-1 py-[2.4vh] rounded-[3vh] bg-[#246CE2] text-[2.2vh] font-bold text-white active:bg-[#1B55B4] transition-colors"
                    >
                        {t("paymentPage.go_home")}
                    </button>
                </div>
            </div>
        );
    }

    // ── Исход печати не подтверждён колбэком ────────────────────────
    if (isWaitingConfirmation) {
        return (
            <div className={`${PAGE} gap-[2vh]`}>
                <div className="flex-1 min-h-0 flex flex-col items-center justify-center text-center">
                    {issueReported ? (
                        <>
                            <motion.div
                                initial={{ scale: 0.6, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.4 }}
                                className="w-[28vh] h-[28vh] bg-white border-[0.3vh] border-[#E1E4EB] rounded-[3vh] p-[2vh] flex items-center justify-center mb-[2.6vh]"
                            >
                                <img
                                    src={botQr}
                                    alt={t("printing.qr_alt")}
                                    className="w-full h-full object-contain"
                                />
                            </motion.div>
                            <h1 className="text-[3.2vh] font-extrabold tracking-tight">
                                {t("paymentPage.issue_title")}
                            </h1>
                            <p className="text-[2vh] font-semibold text-[#7B8290] mt-[1.4vh] max-w-[70vw] leading-snug">
                                {t("paymentPage.issue_sub")}
                            </p>
                            <p className="text-[1.6vh] font-bold text-[#246CE2] mt-[1.4vh]">
                                {t("paymentPage.issue_bot_hint")}: {t("printing.bot_name")}
                            </p>
                        </>
                    ) : (
                        <>
                            <motion.div
                                initial={{ scale: 0.6, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.4 }}
                                className="w-[16vh] h-[16vh] rounded-full bg-[#E3ECFB] flex items-center justify-center mb-[3vh]"
                            >
                                <svg
                                    className="w-[7.6vh] h-[7.6vh]"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#246CE2"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.7-2.5 2-2.5 4" />
                                    <path d="M12 16.5v.01" />
                                    <circle cx="12" cy="12" r="9" />
                                </svg>
                            </motion.div>
                            <h1 className="text-[3.6vh] font-extrabold tracking-tight">
                                {t("paymentPage.outcome_check_title")}
                            </h1>
                            <p className="text-[2vh] font-semibold text-[#7B8290] mt-[1.4vh] max-w-[70vw] leading-snug">
                                {t("paymentPage.outcome_check_sub")}
                            </p>
                        </>
                    )}
                </div>

                <div className="flex-none w-full flex gap-[1.2vw]">
                    {issueReported ? (
                        <button data-analytics="paymentpage.paymentPage.go_home.5"
                            type="button"
                            onClick={handleGoHome}
                            className="flex-1 py-[2.4vh] rounded-[3vh] bg-[#246CE2] text-[2.2vh] font-bold text-white active:bg-[#1B55B4] transition-colors"
                        >
                            {t("paymentPage.go_home")}
                        </button>
                    ) : (
                        <>
                            <button data-analytics="paymentpage.paymentPage.outcome_no.6"
                                type="button"
                                onClick={handleReportIssue}
                                className="flex-1 py-[2.4vh] rounded-[3vh] bg-[#F1F3F7] text-[2.2vh] font-bold text-[#12161F] active:bg-[#E1E4EB] transition-colors"
                            >
                                {t("paymentPage.outcome_no")}
                            </button>
                            <button data-analytics="paymentpage.paymentPage.outcome_yes.7"
                                type="button"
                                onClick={handleConfirmPrinted}
                                className="flex-1 py-[2.4vh] rounded-[3vh] bg-[#246CE2] text-[2.2vh] font-bold text-white active:bg-[#1B55B4] transition-colors"
                            >
                                {t("paymentPage.outcome_yes")}
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // ── Экран 8 · Печать ─────────────────────────────────────────────
    // Подпись статуса — переводом по коду статуса. Раньше показывалась строка
    // из Electron как есть: «Начата печать 1 документов» по-русски на
    // казахском экране.
    const printStatusText = ACTIVE_STATUSES.includes(printStatus)
        ? t(`paymentPage.print_status_${printStatus}`, { defaultValue: t("paymentPage.printing_sub") })
        : t("paymentPage.printing_sub");
    if (showsPrintAds) {
        // Печать с рекламой: слот фиксированного стандарта 3:4 с полями как у
        // карточки статуса (≈1028×1371 на экране киоска 1080×1920) — под этот
        // размер готовятся рекламные макеты. Экран накрывает Header и Footer
        // (fixed на весь экран, z-40 — ниже модалок). Принтера здесь нет —
        // вместо него бегущая полоса: процентов Electron не присылает, поэтому
        // полоса без значения. Реклама одна на все активные статусы (started →
        // submitted → printing), поэтому смена статуса ролик не перезапускает.
        // С приходом completed или error ветка сменяется — показ прерывается и
        // уходит в статистику прерванным, курсор очереди не двигается.
        // «Назад» доступна через пять секунд; печать дальше идёт в Electron.
        return (
            <div className="fixed inset-0 z-40 p-[2.4vw] flex flex-col gap-[1.6vh] bg-[#EDEFF4]">
                <motion.div
                    variants={blockVariants}
                    initial="hidden"
                    animate="visible"
                    custom={0}
                    className="flex-none w-full max-w-[56.25vh] aspect-[3/4] mx-auto"
                >
                    <AdPlayer
                        items={printAds}
                        placement={PLACEMENTS.print}
                        variant="fullscreen"
                        className="w-full h-full rounded-[3vh]"
                    />
                </motion.div>

                <motion.div
                    variants={blockVariants}
                    initial="hidden"
                    animate="visible"
                    custom={1}
                    className={`${CARD} flex-1 min-h-0 px-[4vw] flex flex-col justify-center`}
                >
                    <h3 className="text-[2.6vh] font-extrabold tracking-tight">
                        {t("paymentPage.printing_title")}
                    </h3>
                    <div className="text-[1.7vh] font-semibold text-[#7B8290] mt-[0.5vh]">
                        {printStatusText}
                    </div>
                    <div
                        data-testid="print-progress"
                        role="progressbar"
                        aria-label={t("paymentPage.printing_title")}
                        className="relative mt-[1.8vh] h-[0.9vh] rounded-full bg-[#E3ECFB] overflow-hidden"
                    >
                        <div
                            className="absolute inset-y-0 w-[35%] rounded-full bg-[#246CE2]"
                            style={{ animation: "printBar 1.6s ease-in-out infinite" }}
                        />
                    </div>
                </motion.div>

                <PrintBackControl secondsLeft={printBackSeconds} onBack={handleBackFromPrint} />
            </div>
        );
    }

    if (isActivePrint) {
        return (
            <div className={`${PAGE} gap-[1.6vh]`}>
                <motion.div
                    variants={blockVariants}
                    initial="hidden"
                    animate="visible"
                    custom={0}
                    role="region"
                    aria-label={t("paymentPage.printing_title")}
                    className={`${CARD} flex-1 min-h-0 flex flex-col gap-[1.6vh]`}
                >
                    <div className="flex-1 min-h-0 flex items-center justify-center">
                        <PrinterAnimation />
                    </div>

                    <motion.div
                        variants={blockVariants}
                        initial="hidden"
                        animate="visible"
                        custom={1}
                        className="flex-none px-[3vw] py-[2.4vh] flex items-center justify-center gap-[2vw]"
                    >
                        <span
                            className="flex-none w-[1.8vh] h-[1.8vh] rounded-full bg-[#246CE2]"
                            style={{ animation: "pulse 1.4s ease-in-out infinite" }}
                        />
                        <div className="min-w-0 text-center">
                            <h3 className="text-[2.6vh] font-extrabold tracking-tight">
                                {t("paymentPage.printing_title")}
                            </h3>
                            <div className="text-[1.7vh] font-semibold text-[#7B8290] mt-[0.5vh]">
                                {printStatusText}
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
                <PrintBackControl secondsLeft={printBackSeconds} onBack={handleBackFromPrint} />
            </div>
        );
    }

    // ── QR отсканирован, оплата ещё не подтверждена ─────────────────
    // QR сознательно убран с экрана: он уже открыт в чьём-то приложении
    // банка, второй человек его всё равно не отсканирует, а держать код на
    // виду — только сбивать с толку.
    if (isScanned) {
        return (
            <div className={`${PAGE} gap-[2vh] items-center justify-center`}>
                <motion.div
                    variants={blockVariants}
                    initial="hidden"
                    animate="visible"
                    custom={0}
                    className="flex-1 min-h-0 flex flex-col items-center justify-center text-center"
                >
                    <div className="w-[16vh] h-[16vh] rounded-full bg-[#E3ECFB] flex items-center justify-center mb-[3vh]">
                        <svg
                            className="w-[7.6vh] h-[7.6vh]"
                            style={{ animation: "pulse 1.4s ease-in-out infinite" }}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#246CE2"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M9 12l2 2 4-4" />
                            <circle cx="12" cy="12" r="9" />
                        </svg>
                    </div>
                    <h1 className="text-[3.4vh] font-extrabold tracking-tight">
                        {t("paymentPage.scanned_title")}
                    </h1>
                    <p className="text-[2vh] font-semibold text-[#7B8290] mt-[1.4vh] max-w-[70vw] leading-snug">
                        {t("paymentPage.scanned_sub")}
                    </p>
                    {secondsLeft !== null && (
                        <p className="text-[1.6vh] font-bold text-[#246CE2] mt-[1.8vh]">
                            {t("paymentPage.confirm_in", { time: formatCountdown(secondsLeft) })}
                        </p>
                    )}
                </motion.div>
            </div>
        );
    }

    // failed/expired больше не отдельный экран — см. эффект автообновления
    // QR выше (showToast + showAutoReissueSpinner) и экран 7 ниже.

    // ── Исход платежа неизвестен (payment.lost) ──────────────────────
    // Осознанно без кнопки перевыпуска: деньги могли списаться, а могли и
    // нет — новый QR только запутает и рискует задвоить оплату. Это ровно
    // тот случай, для которого backend уже алертит оператора в Telegram
    // (см. kaspi_qr_webhook.py::_alert_lost_payment).
    if (isPaymentLost) {
        return (
            <div className={`${PAGE} gap-[2vh]`}>
                <motion.div
                    variants={blockVariants}
                    initial="hidden"
                    animate="visible"
                    custom={0}
                    className="flex-1 min-h-0 flex flex-col items-center justify-center text-center"
                >
                    <div className="w-[16vh] h-[16vh] rounded-full bg-[#F7EEDC] flex items-center justify-center mb-[3vh]">
                        <svg
                            className="w-[7.6vh] h-[7.6vh]"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#8A6015"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                        >
                            <circle cx="12" cy="12" r="9" />
                            <path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.7-2.5 2-2.5 4" />
                            <path d="M12 16.5v.01" />
                        </svg>
                    </div>
                    <h1 className="text-[3.4vh] font-extrabold tracking-tight">
                        {t("paymentPage.payment_lost_title")}
                    </h1>
                    <p className="text-[2vh] font-semibold text-[#7B8290] mt-[1.4vh] max-w-[70vw] leading-snug">
                        {t("paymentPage.payment_lost_sub")}
                    </p>
                </motion.div>

                {/* «Назад» в футере на экране оплаты больше нет — без этой
                    кнопки отсюда было бы не уйти до InactivityModal. */}
                <button data-analytics="paymentpage.paymentPage.go_home.9"
                    type="button"
                    onClick={handleGoHome}
                    className="flex-none w-full py-[2.4vh] rounded-[3vh] bg-[#246CE2] text-[2.2vh] font-bold text-white active:bg-[#1B55B4] transition-colors"
                >
                    {t("paymentPage.go_home")}
                </button>
            </div>
        );
    }

    // ── Экран 7 · Оплата ─────────────────────────────────────────────
    return (
        <div className={`${PAGE} gap-[1.6vh] items-center justify-center`}>
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={0}
                className={`${CARD} relative flex-none flex flex-col items-center gap-[1.8vh] px-[6vw] py-[3vh]`}
            >
                {/* bottom, не top: карточка первая на странице, сверху — свободное
                    место до верха экрана, снизу — лента банков и карточка ожидания,
                    там всплывающий тост перекрыл бы их. */}
                <KioskToast message={toastMsg} tone={toastTone} position="bottom" />

                <div className="flex items-center gap-[1vw] text-[2.4vh] font-extrabold">
                    {isUnifiedQr ? (
                        <span className="w-[3.6vh] h-[3.6vh] rounded-[1vh] bg-[#246CE2] text-white flex items-center justify-center">
                            <svg
                                className="w-[2vh] h-[2vh]"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <rect x="3" y="3" width="7" height="7" rx="1.2" />
                                <rect x="14" y="3" width="7" height="7" rx="1.2" />
                                <rect x="3" y="14" width="7" height="7" rx="1.2" />
                                <path d="M14 14h3v3h-3zM20 14v3M14 20h3M20 20v.01" />
                            </svg>
                        </span>
                    ) : (
                        <span className="w-[3.6vh] h-[3.6vh] rounded-[1vh] bg-[#D93A2B] text-white flex items-center justify-center text-[1.9vh]">
                            K
                        </span>
                    )}
                    {t(isUnifiedQr ? "paymentPage.unified_qr" : "paymentPage.kaspi_qr")}
                </div>

                <div className="text-[6vh] font-extrabold tracking-tight leading-none">
                    {payments.amount}{" "}
                    <small className="text-[2.8vh] font-bold text-[#7B8290]">₸</small>
                </div>

                {!showQrError && secondsLeft !== null && (
                    <div className="text-[1.4vh] font-semibold text-[#7B8290] -mt-[1.2vh]">
                        {t("paymentPage.qr_expires_in", { time: formatCountdown(secondsLeft) })}
                    </div>
                )}

                <div className="relative w-[28vh] h-[28vh] bg-white border-[0.3vh] border-[#E1E4EB] rounded-[3vh] p-[1.6vh] flex items-center justify-center">
                    {/* Тихий автоперевыпуск после failed/expired: пока сервер не
                        ответил, старый (уже недействительный) QR остаётся на
                        месте под лёгким затемнением — резкая смена на пустую
                        карточку выглядела бы как поломка. */}
                    {showAutoReissueSpinner && (
                        <div className="absolute inset-[1.6vh] rounded-[2.2vh] bg-white/85 flex items-center justify-center z-10">
                            <svg
                                className="w-[4.4vh] h-[4.4vh]"
                                style={{ animation: "spin 0.8s linear infinite" }}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#246CE2"
                                strokeWidth="2.4"
                                strokeLinecap="round"
                            >
                                <path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />
                            </svg>
                        </div>
                    )}
                    {showQrError ? (
                        <div className="flex flex-col items-center text-center gap-[1.2vh] px-[1.2vh]">
                            <svg
                                className="w-[4.4vh] h-[4.4vh]"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#C2543E"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                            >
                                <circle cx="12" cy="12" r="9" />
                                <path d="M12 7v6M12 16.5v.01" />
                            </svg>
                            <p className="text-[1.5vh] font-bold text-[#C2543E]">
                                {t("paymentPage.qr_error")}
                            </p>
                            {canRetryQr ? (
                                <button data-analytics="paymentpage.paymentPage.qr_retry.10"
                                    type="button"
                                    onClick={handleRetryQr}
                                    disabled={loadingPayment}
                                    className="flex items-center gap-[0.6vw] bg-[#246CE2] text-white rounded-full px-[2.4vw] py-[1.1vh] text-[1.4vh] font-bold disabled:opacity-50 active:bg-[#1B55B4] transition-colors"
                                >
                                    <svg
                                        className="w-[1.8vh] h-[1.8vh] flex-none"
                                        style={
                                            loadingPayment
                                                ? { animation: "spin 0.8s linear infinite" }
                                                : undefined
                                        }
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.4"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />
                                    </svg>
                                    {t("paymentPage.qr_retry")}
                                </button>
                            ) : (
                                <p className="text-[1.25vh] font-semibold text-[#7B8290]">
                                    {t("paymentPage.qr_error_sub")}
                                </p>
                            )}
                        </div>
                    ) : (
                        <img
                            key={imgRetries}
                            src={qrDisplaySrc}
                            alt={t("paymentPage.kaspi_qr")}
                            className="w-full h-full object-contain"
                            onError={() => setQrLoadFailed(true)}
                        />
                    )}
                </div>
            </motion.div>

            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={1}
                className="flex-none w-full mt-[1vh]"
            >
                {isUnifiedQr ? (
                    <BanksMarquee label={t("paymentPage.banks_label")} />
                ) : (
                    // Прежний способ (SmartVend) — QR читается только в Kaspi.kz,
                    // список банков здесь был бы вводящим в заблуждение.
                    <div className="flex flex-col items-center text-center gap-[0.4vh] py-[1vh]">
                        <div className="flex items-center gap-[0.8vw] text-[1.6vh] font-bold text-[#12161F]">
                            <span className="w-[2.6vh] h-[2.6vh] rounded-[0.7vh] bg-[#D93A2B] text-white flex items-center justify-center text-[1.3vh] flex-none">
                                K
                            </span>
                            {t("paymentPage.kaspi_only")}
                        </div>
                        <p className="text-[1.3vh] font-semibold text-[#7B8290]">
                            {t("paymentPage.kaspi_only_note")}
                        </p>
                    </div>
                )}
            </motion.div>

            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={2}
                className={`${CARD} flex-none flex flex-col items-center text-center px-[4vw] py-[2.2vh] mt-[1vh]`}
            >
                <div className="flex items-center gap-[1vw] text-[2vh] font-bold">
                    <span
                        className="w-[1.4vh] h-[1.4vh] rounded-full bg-[#246CE2] flex-none"
                        style={{ animation: "pulse 1.4s ease-in-out infinite" }}
                    />
                    {t(
                        // Оплата уже подтверждена опросом — печать вот-вот
                        // начнётся (Electron ещё не прислал печать-статус,
                        // иначе isActivePrint выше уже перехватил бы рендер).
                        currentPaymentStatus === "completed"
                            ? "paymentPage.payment_confirmed"
                            : "paymentPage.waiting_payment",
                    )}
                </div>
                <p className="text-[1.6vh] font-semibold text-[#7B8290] mt-[0.8vh]">
                    {t("paymentPage.receipt_note")}
                </p>
            </motion.div>

            {canCancelPayment && (
                <motion.div
                    variants={blockVariants}
                    initial="hidden"
                    animate="visible"
                    custom={3}
                    className="flex-none flex justify-center mt-[1vh]"
                >
                    <button data-analytics="paymentpage.paymentPage.cancel.11"
                        type="button"
                        onClick={handleCancelPayment}
                        disabled={cancelling || loadingPayment}
                        className="flex items-center gap-[1vw] bg-white rounded-full shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)] px-[5vw] py-[1.6vh] text-[#12161F] font-sans font-bold text-[2.2vh] disabled:opacity-50 active:bg-[#F1F3F7] transition-colors"
                    >
                        <svg
                            className="w-[2.4vh] h-[2.4vh]"
                            style={cancelling ? { animation: "spin 0.8s linear infinite" } : undefined}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            {cancelling ? (
                                <path d="M21 12a9 9 0 1 1-2.6-6.4" />
                            ) : (
                                <path d="M6 6l12 12M18 6L6 18" />
                            )}
                        </svg>
                        {t("paymentPage.cancel")}
                    </button>
                </motion.div>
            )}
        </div>
    );
}

export default PaymentPage;
